import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { createCheckoutSession } from "@/lib/subscriptions.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useLocale } from "@/lib/i18n";
import { planPriceIdFor } from "@/lib/pricing";


export const Route = createFileRoute("/assinar")({
  head: () => ({
    meta: [
      { title: "Seja Embaixador — Viny Club" },
      {
        name: "description",
        content:
          "Assine o Viny Pro por R$ 9,90/mês: scroll infinito no Explorar, perfil em destaque, templates exclusivos do mini site, selo de Embaixador e acesso antecipado às novidades.",
      },
      { property: "og:title", content: "Seja Embaixador — Viny Club" },
      {
        property: "og:description",
        content:
          "Scroll infinito no Explorar, perfil em destaque, templates exclusivos e selo de Embaixador. Ajude a construir o futuro da Viny.",
      },

      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AssinarPage,
});

function AssinarPage() {
  const navigate = Route.useNavigate();
  const { isAuthenticated, loading } = useCurrentUser();
  const { locale } = useLocale();
  const checkout = useServerFn(createCheckoutSession);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (loading || !isAuthenticated || started.current) return;
    started.current = true;

    (async () => {
      try {
        const origin = window.location.origin;
        const result = await checkout({
          data: {
            priceId: planPriceIdFor(locale),
            returnUrl: `${origin}/settings/billing?status=success&session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${origin}/settings/billing?status=canceled`,
            environment: getStripeEnvironment(),
          },
        });
        if ("error" in result) throw new Error(result.error);
        if (!result.url) throw new Error("Stripe não retornou o link de pagamento");
        window.location.href = result.url;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Não foi possível abrir o checkout");
      }
    })();
  }, [loading, isAuthenticated, checkout, locale]);


  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-white text-zinc-950">
      <PaymentTestModeBanner />

      <header className="flex shrink-0 items-center gap-3 border-b border-zinc-200 px-4 py-3 sm:px-6">
        <button
          type="button"
          onClick={() => {
            if (typeof window !== "undefined" && window.history.length > 1)
              window.history.back();
            else navigate({ to: "/explore" });
          }}
          className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-500 transition hover:text-zinc-950"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar
        </button>
        <div className="ml-auto inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
          <Lock className="h-3.5 w-3.5" /> Checkout seguro
          <span className="inline-flex items-center rounded-md bg-[#635BFF] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
            stripe
          </span>
        </div>
      </header>

      <main className="flex min-h-0 flex-1 items-center justify-center px-6">
        {loading ? (
          <p className="text-sm text-zinc-500">Carregando…</p>
        ) : !isAuthenticated ? (
          <div className="text-center">
            <p className="text-sm text-zinc-600">
              Entre na sua conta para concluir a assinatura.
            </p>
            <Link
              to="/auth"
              className="mt-4 inline-flex items-center justify-center rounded-full bg-zinc-950 px-5 py-3 text-sm font-bold text-white"
            >
              Entrar ou criar conta
            </Link>
          </div>
        ) : error ? (
          <div className="text-center">
            <p className="text-sm font-semibold text-red-600">{error}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 inline-flex items-center justify-center rounded-full bg-zinc-950 px-5 py-3 text-sm font-bold text-white"
            >
              Tentar novamente
            </button>
          </div>
        ) : (
          <div className="text-center">
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-zinc-400" />
            <p className="mt-3 text-sm text-zinc-600">
              Redirecionando para o pagamento seguro da Stripe…
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

export default AssinarPage;
